/**
 * components/ProjectLocate.jsx — SC-4.
 *
 * Project-aware "Where am I?" overlay launched from the SheetViewer.
 * Distinct from the legacy operational LocateControl (which uses
 * lat/lng anchors per-sheet). This one uses SC-1 settings + SC-3
 * sheet-to-project affine to give true project positioning.
 *
 * Plain-English rule: zero EPSG / WKT / matrix references in the UI.
 * The operator sees:
 *   "Located on: C-102"
 *   "Position Accuracy: ±4 ft"
 *   "Calibration: HIGH"
 *   "GPS: GOOD"
 *
 * Honest failure handling:
 *   - Project not configured       → "Open Project Survey Control"
 *   - Sheet not calibrated         → "This sheet has not been calibrated…"
 *   - GPS poor                     → "Position may be off by N feet…"
 *   - Custom/Local Grid projection → "use I'm Here to drop manually"
 *   - Off-project                  → "Outside every calibrated sheet"
 *
 * Manual fallback (per spec): tap "I'm Here" → next click on the sheet
 * records a MANUAL_POSITION badge — never relabeled as GPS.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Compass, MapPin, AlertTriangle, CheckCircle2, Crosshair, X, ArrowRight, Hand } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";


const CONF_STYLES = {
  high:   { color: "rgb(34,197,94)",  bg: "rgba(34,197,94,0.10)",  border: "rgba(34,197,94,0.55)",  label: "HIGH" },
  medium: { color: "rgb(245,158,11)", bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.55)", label: "MEDIUM" },
  low:    { color: "rgb(239,68,68)",  bg: "rgba(239,68,68,0.10)",  border: "rgba(239,68,68,0.55)",  label: "LOW" },
};


/**
 * Returns geolocation as a Promise with a hard timeout. Browser
 * `getCurrentPosition` can hang indefinitely without one.
 */
function readGeolocation({ timeoutMs = 12000 } = {}) {
  return new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return reject(new Error("Location services unavailable on this device"));
    }
    let done = false;
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      reject(new Error("GPS timed out — make sure Location is enabled"));
    }, timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          heading: pos.coords.heading != null && !Number.isNaN(pos.coords.heading)
            ? pos.coords.heading : null,
        });
      },
      (err) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        const msg = err.code === 1
          ? "Location permission denied. Enable GPS for this site in your browser."
          : err.code === 2
            ? "Couldn't get a GPS fix. Move to open sky and try again."
            : err.code === 3
              ? "GPS timed out — try again."
              : err.message || "Location services error";
        reject(new Error(msg));
      },
      { enableHighAccuracy: true, timeout: timeoutMs - 1000, maximumAge: 0 },
    );
  });
}


export default function ProjectLocate({
  open, onClose,
  projectId,
  sheetId,
  sheetNumber,
  // Called when the operator switches to a different sheet.
  onSwitchSheet,
  // Called with {nx, ny, accuracy_in_units, kind:'gps'|'manual', confidence}
  // so the SheetViewer can render a marker on the canvas.
  onPosition,
}) {
  const [readiness, setReadiness] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [manualMode, setManualMode] = useState(false);
  const navigate = useNavigate();

  // Probe readiness whenever the modal opens.
  useEffect(() => {
    let cancelled = false;
    if (!open || !projectId) return;
    setError(""); setResult(null); setManualMode(false);
    (async () => {
      try {
        const r = await api.get(`/projects/${projectId}/positioning-readiness`);
        if (!cancelled) setReadiness(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [open, projectId]);

  const locate = async () => {
    setBusy(true); setError(""); setResult(null);
    try {
      const fix = await readGeolocation();
      const { data } = await api.post(`/projects/${projectId}/locate`, fix);
      setResult({ ...data, _gps_fix: fix });
      // Push position to canvas if we landed on the current sheet.
      if (data?.located && !data?.off_project && data?.primary && onPosition) {
        const onCurrent = data.primary.sheet_id === sheetId;
        onPosition({
          nx: data.primary.nx, ny: data.primary.ny,
          accuracy_in_units: data.accuracy_in_units,
          horizontal_units_label: data.horizontal_units_label,
          confidence: data.confidence,
          kind: "gps",
          on_current_sheet: onCurrent,
        });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const startManual = () => {
    setManualMode(true);
    setResult(null);
    setError("");
    if (onPosition) onPosition({ enterManualMode: true });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-2 sm:p-4"
          data-testid="project-locate-overlay" onClick={onClose}>
      <div className="bg-[var(--fop-bg)] border border-[var(--fop-border)] rounded-lg w-full max-w-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-[var(--fop-border)] flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Compass size={18} className="text-[var(--fop-primary)] shrink-0" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">Project Locate</div>
              <div className="font-display text-base text-[var(--fop-text)] leading-tight">Where am I?</div>
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" data-testid="project-locate-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {/* READINESS GATING */}
          {readiness && !readiness.ready && !manualMode && (
            <NotReadyBanner readiness={readiness} projectId={projectId} sheetId={sheetId} navigate={navigate} onClose={onClose} onStartManual={startManual} />
          )}

          {/* CTA — always show GPS button + I'm Here, both functional */}
          {!result && (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={locate}
                disabled={busy || !readiness?.ready}
                className="btn btn-primary text-sm inline-flex items-center justify-center gap-1.5"
                data-testid="locate-gps-btn"
                title={!readiness?.ready ? "Project must be configured & at least one sheet calibrated" : ""}
              >
                <Crosshair size={14} /> {busy ? "Locating…" : "Use GPS"}
              </button>
              <button
                onClick={startManual}
                className="btn btn-ghost text-sm inline-flex items-center justify-center gap-1.5"
                data-testid="locate-manual-btn"
              >
                <Hand size={14} /> I&apos;m Here
              </button>
            </div>
          )}

          {manualMode && !result && (
            <ManualHint onCancel={() => { setManualMode(false); if (onPosition) onPosition({ enterManualMode: false }); }} />
          )}

          {error && (
            <div className="rounded p-3 border border-[rgba(239,68,68,0.4)] bg-[rgba(239,68,68,0.08)] text-sm text-[rgb(239,68,68)]"
                  data-testid="project-locate-error">
              {error}
            </div>
          )}

          {result && (
            <ResultBlock
              result={result}
              currentSheetId={sheetId}
              onSwitchSheet={(target) => { onSwitchSheet?.(target); onClose(); }}
              onClose={onClose}
              onTryManual={startManual}
            />
          )}
        </div>
      </div>
    </div>
  );
}


function NotReadyBanner({ readiness, projectId, sheetId, navigate, onClose, onStartManual }) {
  const reason = readiness?.reason;
  let cta = null;
  if (reason === "settings_unconfigured") {
    cta = { label: "Open Project Survey Control", to: `/project/${projectId}/survey-control` };
  } else if (reason === "no_calibrated_sheets") {
    cta = { label: "Calibrate this sheet now", to: `/sheet/${sheetId}/calibrate` };
  }
  return (
    <div className="rounded p-3 border-2 bg-[rgba(245,158,11,0.08)] border-[rgba(245,158,11,0.45)]"
          data-testid="project-locate-not-ready">
      <div className="flex gap-2">
        <AlertTriangle size={16} className="text-[rgb(245,158,11)] shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm text-[var(--fop-text)] mb-1">Auto-locate isn&apos;t ready yet.</div>
          <div className="text-xs text-[var(--fop-text-2)]">{readiness.reason_label}</div>
          <div className="flex gap-2 mt-2 flex-wrap">
            {cta && (
              <button onClick={() => { navigate(cta.to); onClose(); }} className="btn btn-primary text-xs inline-flex items-center gap-1" data-testid="not-ready-cta">
                {cta.label} <ArrowRight size={12} />
              </button>
            )}
            <button onClick={onStartManual} className="btn btn-ghost text-xs inline-flex items-center gap-1" data-testid="not-ready-fallback-manual">
              <Hand size={12} /> Use I&apos;m Here instead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function ManualHint({ onCancel }) {
  return (
    <div className="rounded p-3 border-2 bg-[var(--fop-primary-soft)] border-[var(--fop-primary)] flex items-start gap-2" data-testid="manual-hint">
      <Crosshair size={14} className="text-[var(--fop-primary)] shrink-0 mt-1" />
      <div className="text-sm text-[var(--fop-text)] flex-1">
        <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-primary)] mb-0.5">Manual position</div>
        Tap your location on the sheet. We&apos;ll record it as <b>MANUAL POSITION</b> &mdash; never confused with GPS.
      </div>
      <button onClick={onCancel} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" data-testid="cancel-manual-hint">
        <X size={14} />
      </button>
    </div>
  );
}


function ResultBlock({ result, currentSheetId, onSwitchSheet, onClose, onTryManual }) {
  if (result.off_project) {
    return (
      <div className="rounded p-3 border bg-[rgba(239,68,68,0.06)] border-[rgba(239,68,68,0.4)]" data-testid="result-off-project">
        <div className="text-sm text-[rgb(239,68,68)] mb-1">Off project</div>
        <div className="text-xs text-[var(--fop-text-2)]">
          Your GPS reading is outside every calibrated sheet on this project.
          {result.gps_quality?.label === "poor" && " GPS is poor — try moving to open sky."}
        </div>
        <button onClick={onTryManual} className="btn btn-ghost text-xs mt-2 inline-flex items-center gap-1" data-testid="off-project-manual">
          <Hand size={12} /> Drop where I am instead
        </button>
      </div>
    );
  }

  if (!result.primary) return null;
  const conf = CONF_STYLES[result.confidence] || CONF_STYLES.low;
  const onCurrent = result.primary.sheet_id === currentSheetId;
  return (
    <div className="space-y-2" data-testid="result-block">
      <div className="rounded-lg p-3 border-2"
            style={{ background: conf.bg, borderColor: conf.border }}
            data-testid="result-located-card">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">Located on</div>
            <div className="font-display text-2xl text-[var(--fop-text)] leading-tight" data-testid="result-sheet-number">
              {result.primary.sheet_number}
            </div>
            <div className="text-xs text-[var(--fop-text-3)] truncate">{result.primary.sheet_title}</div>
          </div>
          <span className="px-2 py-0.5 rounded font-mono text-[11px] uppercase tracking-wider shrink-0"
                  style={{ background: conf.bg, color: conf.color, border: `1px solid ${conf.border}` }}
                  data-testid="result-confidence-pill">
            {conf.label}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
          <Stat
            label="Position Accuracy"
            value={result.accuracy_in_units != null
              ? `±${Math.max(1, Math.round(result.accuracy_in_units))} ${result.horizontal_units_label}`
              : "—"}
            testid="result-position-accuracy"
          />
          <Stat
            label="GPS Quality"
            value={result.gps_quality?.label?.toUpperCase() || "UNKNOWN"}
            testid="result-gps-quality"
          />
          <Stat
            label="Calibration"
            value={(result.primary.calibration_confidence || "—").toUpperCase()}
            testid="result-calibration"
          />
          <Stat
            label="Confidence"
            value={conf.label}
            testid="result-overall-confidence"
          />
        </div>
        {!onCurrent && (
          <div className="mt-3 rounded p-2 bg-[var(--fop-surface-2)] flex items-center gap-2" data-testid="result-switch-prompt">
            <AlertTriangle size={12} className="text-[rgb(245,158,11)] shrink-0" />
            <div className="text-xs text-[var(--fop-text-2)] flex-1">
              You&apos;re on a different sheet right now. Switch to <b>{result.primary.sheet_number}</b>?
            </div>
            <button onClick={() => onSwitchSheet({ sheetId: result.primary.sheet_id })}
                    className="btn btn-primary text-xs inline-flex items-center gap-1"
                    data-testid="result-switch-sheet">
              Switch <ArrowRight size={12} />
            </button>
          </div>
        )}
      </div>

      {/* SC-5 — Station / Offset block. Only renders when alignments exist
          on the resolved sheet and the resolver returned a station. */}
      {result.station && (
        <div className="rounded p-3 border-2 bg-[var(--fop-surface-2)] border-[var(--fop-primary)]"
              data-testid="result-station-block">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">Alignment</div>
          <div className="text-sm text-[var(--fop-text)] truncate" data-testid="result-alignment-name">
            {result.station.alignment_name}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
            <Stat label="Station" value={result.station.station_label || "—"} testid="result-station-label" />
            <Stat label="Offset"  value={result.station.offset_label  || "—"} testid="result-offset-label"  />
          </div>
          <div className="mt-2 text-[11px] text-[var(--fop-text-2)]" data-testid="result-station-friendly">
            You are at {result.station.station_label}
            {result.station.offset_label ? `, ${result.station.offset_label} of ` : " on "}
            {result.station.alignment_name}.
          </div>
        </div>
      )}
      {!result.station && result.primary && (
        <div className="rounded p-2 border border-[var(--fop-border)] bg-[var(--fop-surface)] text-[11px] text-[var(--fop-text-3)]"
              data-testid="result-no-alignment">
          No alignment near your position. Add one in <span className="text-[var(--fop-text-2)]">Survey Control → Alignments</span> to see station / offset here.
        </div>
      )}

      {/* Other candidates */}
      {(result.candidates_in_bounds.length > 1 || result.candidates_near.length > 0) && (
        <div className="rounded p-2 border border-[var(--fop-border)] bg-[var(--fop-surface)]" data-testid="result-other-sheets">
          <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Also visible on</div>
          <div className="flex flex-wrap gap-1">
            {[...result.candidates_in_bounds.slice(1), ...result.candidates_near].map(c => (
              <button key={c.sheet_id}
                      onClick={() => onSwitchSheet({ sheetId: c.sheet_id })}
                      className="px-2 py-1 rounded text-[11px] font-mono bg-[var(--fop-surface-2)] hover:bg-[var(--fop-primary-soft)]"
                      data-testid={`result-other-${c.sheet_number}`}>
                {c.sheet_number}
                {!c.in_bounds && <span className="ml-1 text-[var(--fop-text-3)]">(near)</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Advisories */}
      {result.advisories?.length > 0 && (
        <div className="rounded p-2 border border-[rgba(245,158,11,0.4)] bg-[rgba(245,158,11,0.08)]" data-testid="result-advisories">
          {result.advisories.map((a, i) => (
            <div key={i} className="text-xs text-[rgb(245,158,11)] flex gap-1.5">
              <AlertTriangle size={11} className="shrink-0 mt-0.5" />
              <span>{a}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function Stat({ label, value, testid }) {
  return (
    <div className="rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] p-2">
      <div className="font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)]">{label}</div>
      <div className="font-mono text-[var(--fop-text)] text-sm" data-testid={testid}>{value}</div>
    </div>
  );
}
